import { useEffect, useRef, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { api, formatApiErrorDetail } from "../../lib/api";

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function ProjectRefinePanel({ open, onClose, projectId, plan, initialConversation,
  dirty, onRefined }) {
  const [conversation, setConversation] = useState(initialConversation || []);
  const [input, setInput] = useState("");
  const [lastRequest, setLastRequest] = useState("");
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);

  useEffect(() => { setConversation(initialConversation || []); }, [initialConversation]);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  if (!open) return null;

  const send = async () => {
    const message = input.trim();
    if (!message || sending || generating) return;
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/chat`, { message });
      if (!active.current) return;
      setConversation(data.conversation || []);
      setLastRequest(message);
      setInput("");
    } catch (exc) {
      setError(formatApiErrorDetail(exc.response?.data?.detail) || exc.message);
    } finally {
      if (active.current) setSending(false);
    }
  };

  const refine = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    setError("");
    setStage("Starting generation");
    try {
      const { data } = await api.post(`/projects/${projectId}/generate-from-chat/async`,
        { instruction: lastRequest });
      while (active.current) {
        await pause(2000);
        if (!active.current) return;
        const { data: job } = await api.get(`/generation-jobs/${data.job_id}`);
        setStage(job.stage_label || "Generating architecture");
        if (job.status === "failed") throw new Error(job.error || "Generation failed");
        if (job.status === "done") {
          await onRefined();
          setLastRequest("");
          setStage("");
          return;
        }
      }
    } catch (exc) {
      if (active.current) setError(formatApiErrorDetail(exc.response?.data?.detail) || exc.message);
    } finally {
      if (active.current) setGenerating(false);
    }
  };

  return (
    <>
      <aside role="dialog" aria-label="Project advisor" data-testid="project-refine-panel"
        className="fixed right-0 bottom-0 z-40 flex flex-col border-l shadow-2xl"
        style={{ top: 64, width: "min(420px, 100vw)", background: "var(--color-background-primary)",
          borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}>
        <div className="flex items-center justify-between border-b px-5 py-4"
          style={{ borderColor: "var(--color-border-primary)" }}>
          <div>
            <h2 className="text-sm font-semibold">Project advisor</h2>
            <p className="mt-1 text-xs" style={{ color: "var(--color-text-secondary)" }}>
              Ask about this design, then choose whether to refine it.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close project advisor"
            className="rounded-md p-2 focus-visible:outline focus-visible:outline-2"
            style={{ color: "var(--color-text-secondary)" }}><X size={17} /></button>
        </div>
        {plan === "free" ? (
          <div className="p-5 text-sm">
            <p>Project Q&amp;A and refinement are available on Pro.</p>
            <a href="/pricing" className="mt-4 inline-block underline" style={{ color: "var(--color-accent)" }}>
              View plans
            </a>
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4" aria-live="polite">
              {conversation.length === 0 && (
                <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                  Ask why a service was chosen, or describe a change you want to explore.
                  Chat alone never changes your architecture.
                </p>
              )}
              {conversation.map((turn, index) => (
                <div key={`${index}-${turn.ts || ""}`} className="mb-4 text-sm">
                  <p className="mb-1 text-xs font-semibold" style={{ color: "var(--color-text-secondary)" }}>
                    {turn.role === "user" ? "You" : "Guided Sentry"}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{turn.text}</p>
                </div>
              ))}
            </div>
            <div className="border-t p-4" style={{ borderColor: "var(--color-border-primary)" }}>
              {error && <p role="alert" className="mb-3 text-xs" style={{ color: "var(--color-danger)" }}>{error}</p>}
              {generating && <p role="status" className="mb-3 flex items-center gap-2 text-xs">
                <Loader2 size={14} className="animate-spin" /> {stage}
              </p>}
              {lastRequest && !generating && (
                <div className="mb-3">
                  <button type="button" data-testid="confirm-refine-open"
                    disabled={dirty} onClick={() => setConfirmOpen(true)}
                    className="rounded-md px-3 py-2 text-xs font-semibold disabled:opacity-50"
                    style={{ background: "var(--color-accent)", color: "#0a0a0a" }}>
                    Regenerate using my last request
                  </button>
                  {dirty && <p className="mt-1 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                    Save or discard unsaved edits before regenerating.
                  </p>}
                </div>
              )}
              <label htmlFor="project-advisor-input" className="sr-only">Message project advisor</label>
              <textarea id="project-advisor-input" data-testid="project-advisor-input"
                value={input} onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault(); send();
                } }} rows={3} maxLength={2000} disabled={sending || generating}
                placeholder="Ask a question or describe a change…"
                className="w-full resize-none rounded-md border p-3 text-sm focus-visible:outline focus-visible:outline-2"
                style={{ background: "var(--color-background-secondary)",
                  borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }} />
              <button type="button" onClick={send} disabled={!input.trim() || sending || generating}
                data-testid="project-advisor-send" className="mt-2 flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold disabled:opacity-50"
                style={{ background: "var(--color-accent)", color: "#0a0a0a" }}>
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
              </button>
            </div>
          </>
        )}
      </aside>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent style={{ background: "var(--color-background-primary)",
          color: "var(--color-text-primary)", borderColor: "var(--color-border-primary)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate this architecture?</AlertDialogTitle>
            <AlertDialogDescription>
              Guided Sentry will build a new design using your last request. This replaces the current
              saved architecture, including manual edits, but keeps the previous version in history.
              Nothing is deployed to your cloud.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-refine" onClick={refine}>Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
